/**
 * PushNotificationHandler
 * Initializes push notifications on native platforms
 * and handles incoming notifications
 */
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import usePushNotifications from '../hooks/usePushNotifications';
import { toast } from 'sonner';

export default function PushNotificationHandler() {
  const navigate = useNavigate();
  const {
    isSupported,
    onNotificationReceived,
    onNotificationTapped
  } = usePushNotifications();

  // Push permission is intentionally NOT requested automatically here.
  // enablePush()'s device-token registration is never sent to the backend
  // (there is no backend endpoint to store it yet), so no push can ever
  // actually be delivered — auto-requesting on every authenticated native
  // launch would burn the user's one OS permission prompt on a feature that
  // currently does nothing (see AGENTS.md pre-launch audit, finding NAT-2).
  // Wire device-token registration to a real backend endpoint, then restore
  // an explicit-action or startup call to usePushNotifications().enablePush().

  useEffect(() => {
    if (!isSupported) return;

    // Handle notification received while app is in foreground
    const unsubReceived = onNotificationReceived((notification) => {
      console.log('[Push] Notification received:', notification);
      
      // Show a toast for foreground notifications
      toast.info(notification.title || 'New notification', {
        description: notification.body,
      });
    });

    // Handle notification tap (app opened from notification)
    const unsubTapped = onNotificationTapped((action) => {
      console.log('[Push] Notification tapped:', action);
      
      const data = action.notification?.data;
      
      // Handle deep linking based on notification data
      if (data?.route) {
        navigate(data.route);
      } else if (data?.type === 'meal_reminder') {
        navigate('/plan');
      } else if (data?.type === 'grocery') {
        navigate('/grocery');
      } else if (data?.type === 'progress') {
        navigate('/progress');
      }
    });

    return () => {
      unsubReceived();
      unsubTapped();
    };
  }, [isSupported, onNotificationReceived, onNotificationTapped, navigate]);

  // This component doesn't render anything
  return null;
}
